import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'
import { useBrand, splitBrandName } from '@/hooks/useBrand'
import { CheckCircle2, AlertTriangle, Loader2, Sparkles, ArrowRight, Building2 } from 'lucide-react'

const ACCESS_CODE_KEY = 'app_access_code'
const ACCESS_CODE_META_KEY = 'app_access_code_meta'
const AUTH_TOKEN_KEY = 'app_auth_token'
const AGENT_DATA_KEY = 'app_agent_data'

interface RedemptionMeta {
  codeId: string
  label: string
  tier: string
  durationDays: number
  partnerEmail: string | null
}

const TIER_LABELS: Record<string, string> = {
  associate: 'Associate',
  partner: 'Partner',
  principal: 'Principal',
}

// Four resolved states once we've cross-referenced the code with the
// browser's auth state. Each maps to a distinct UI:
//   - signup:        not logged in + non-partner code → existing signup flow
//   - partner-login: not logged in + partner code → "sign in to redeem"
//                    (the partner_email already has an account, signup
//                    would just 409 — bounce them through login instead)
//   - partner:       logged in AND the code's partner_email matches them
//   - mismatch:      logged in but the code is for a DIFFERENT email
type Mode = 'signup' | 'partner-login' | 'partner' | 'mismatch'

export default function RedeemPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const brand = useBrand()
  const brandBaseName = splitBrandName(brand.appName)[0]
  const code = (searchParams.get('code') || '').trim().toUpperCase()

  const [state, setState] = useState<'loading' | 'valid' | 'invalid'>('loading')
  const [meta, setMeta] = useState<RedemptionMeta | null>(null)
  const [mode, setMode] = useState<Mode>('signup')
  const [error, setError] = useState<string>('')

  // Agency-partner submission state
  const [orgName, setOrgName] = useState('')
  const [website, setWebsite] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')

  // Match <html> AND <body> background to the auth gradient so iOS
  // rubber-band overscroll doesn't flash the light app background — same
  // pattern LoginPage uses.
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtml = html.style.backgroundColor
    const prevBody = body.style.backgroundColor
    html.style.backgroundColor = '#0d0a1f'
    body.style.backgroundColor = '#0d0a1f'
    return () => {
      html.style.backgroundColor = prevHtml
      body.style.backgroundColor = prevBody
    }
  }, [])

  useEffect(() => {
    if (!code) {
      setState('invalid')
      setError('No code provided. Check the link you were sent.')
      return
    }
    let cancelled = false
    api
      .post('/api/access-codes/redeem', { code })
      .then((res) => {
        if (cancelled) return
        if (res.data?.success && res.data?.data) {
          const m: RedemptionMeta = res.data.data
          setMeta(m)
          setState('valid')

          // Cross-reference with browser auth state to pick the right flow.
          // A partner code (partner_email set) implies the recipient already
          // has an account — going through signup would just 409. So if
          // they're logged out we send them through login instead, then
          // back to /redeem so the partner flow can complete.
          const token = localStorage.getItem(AUTH_TOKEN_KEY)
          const agentRaw = localStorage.getItem(AGENT_DATA_KEY)
          const isLoggedIn = !!(token && agentRaw)
          if (m.partnerEmail) {
            if (isLoggedIn) {
              try {
                const agent = JSON.parse(agentRaw!)
                const callerEmail = (agent?.email || '').toLowerCase()
                if (callerEmail === m.partnerEmail.toLowerCase()) {
                  setMode('partner')
                } else {
                  setMode('mismatch')
                }
              } catch {
                setMode('partner-login')
              }
            } else {
              setMode('partner-login')
            }
          } else {
            setMode('signup')
          }
        } else {
          setState('invalid')
          setError('Code response was unexpected. Please ask your contact for a new link.')
        }
      })
      .catch((err) => {
        if (cancelled) return
        setState('invalid')
        setError(err?.response?.data?.message || 'This code could not be redeemed.')
      })
    return () => { cancelled = true }
  }, [code])

  function handleContinueSignup() {
    if (!meta) return
    // localStorage (not sessionStorage) so the code survives the email-
    // verification flow — clicking the link in the verification email
    // typically opens a new tab/window, which would have an empty
    // sessionStorage. Cleared after consumption in OnboardingPage.
    localStorage.setItem(ACCESS_CODE_KEY, code)
    localStorage.setItem(ACCESS_CODE_META_KEY, JSON.stringify(meta))
    navigate('/onboarding/step/1')
  }

  async function handlePartnerCreate() {
    if (!meta || submitting) return
    if (!orgName.trim()) {
      setSubmitError('Enter the firm name.')
      return
    }
    setSubmitting(true)
    setSubmitError('')
    try {
      const res = await api.post('/api/access-codes/redeem-as-agency-partner', {
        code,
        organizationName: orgName.trim(),
        website: website.trim() || undefined,
      })
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Could not redeem this code.')
      }
      // Hard reload into the dashboard with the new org-scoped token. The
      // useAuth hook re-reads localStorage on mount, so a hard navigation
      // is the simplest way to flip the whole app into the new org without
      // plumbing setState across module boundaries.
      localStorage.setItem(AUTH_TOKEN_KEY, res.data.token)
      // Clear stashed signup-path code so it can't be reused.
      localStorage.removeItem(ACCESS_CODE_KEY)
      localStorage.removeItem(ACCESS_CODE_META_KEY)
      window.location.href = '/dashboard'
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.message || 'Could not redeem this code.')
      setSubmitting(false)
    }
  }

  const tierLabel = meta ? (TIER_LABELS[meta.tier] ?? meta.tier) : ''

  // Headline + sub vary by mode so the page doesn't read like a generic
  // signup invite when the user is actually adding a client to their account.
  const headline =
    state === 'valid' && mode === 'partner' ? (
      <>
        Add a new firm to
        <br />
        <span className="text-brand-periwinkle">your {brandBaseName} account</span>
      </>
    ) : state === 'valid' && mode === 'partner-login' ? (
      <>
        Sign in to add
        <br />
        <span className="text-brand-periwinkle">a new firm</span>
      </>
    ) : state === 'valid' && mode === 'mismatch' ? (
      <>This invite is for a different email</>
    ) : state === 'valid' ? (
      <>
        You've been invited to try
        <br />
        <span className="text-brand-periwinkle">{brandBaseName}</span>
      </>
    ) : state === 'invalid' ? (
      <>Couldn't redeem this code</>
    ) : (
      <>Validating your invite…</>
    )

  const sub =
    state === 'valid' && mode === 'partner'
      ? `You'll be added as admin of the new firm with ${tierLabel}-tier access for ${meta?.durationDays} days. Switch between firms anytime from the org switcher.`
      : state === 'valid' && mode === 'partner-login'
      ? `This invite is for ${meta?.partnerEmail}. Sign in with that email to set up the new firm under your existing account.`
      : state === 'valid' && mode === 'mismatch'
      ? `This code was issued to ${meta?.partnerEmail}. Sign out and sign back in with that email to redeem it.`
      : state === 'valid'
      ? `Your full ${tierLabel}-tier demo is ready — onboard a real law firm website and see what your AI receptionist can do. No card required.`
      : state === 'invalid'
      ? 'The link may have expired or already been redeemed. Reach out to whoever sent it for a fresh invite.'
      : 'Just a moment while we look up your code.'

  return (
    <AuthShell headline={headline} sub={sub}>
      {state === 'loading' && (
        <div
          className="flex items-center justify-center py-16 text-white/70 opacity-0 animate-fade-in-up"
          style={{ animationDelay: '60ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          <span className="text-sm">Checking your code…</span>
        </div>
      )}

      {/* Logged-in agency partner — fast path to spin up a new client org */}
      {state === 'valid' && mode === 'partner' && meta && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/20 text-violet-200 text-xs font-bold uppercase tracking-wider mb-5">
            <Building2 className="h-3.5 w-3.5" />
            Agency partner · {tierLabel} · {meta.durationDays} days
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 mb-5">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45 mb-2">Invite reference</p>
            <p className="text-lg font-semibold text-white">{meta.label}</p>
            <p className="text-xs text-white/55 mt-1">You'll be admin of the new firm — invite their team and get them set up.</p>
          </div>

          <div className="space-y-4 mb-5">
            <div>
              <Label htmlFor="orgName" className="text-white/85 text-sm">Firm name</Label>
              <Input
                id="orgName"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g. Smith & Associates"
                className="mt-1.5 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30"
                autoFocus
              />
            </div>
            <div>
              <Label htmlFor="website" className="text-white/85 text-sm">Firm website <span className="text-white/40">(optional)</span></Label>
              <Input
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
                className="mt-1.5 bg-white/[0.04] border-white/10 text-white placeholder:text-white/30"
              />
              <p className="text-[11px] text-white/40 mt-1">Used to pre-fill agent prompts and branding from the firm's site.</p>
            </div>
          </div>

          {submitError && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/[0.08] p-3 mb-4 text-sm text-rose-200">
              {submitError}
            </div>
          )}

          <Button
            onClick={handlePartnerCreate}
            disabled={submitting || !orgName.trim()}
            className="w-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 h-11 group disabled:opacity-50"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating firm…</>
            ) : (
              <>Add firm to my account <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} /></>
            )}
          </Button>
        </div>
      )}

      {/* Logged in but the code is for someone else's email */}
      {state === 'valid' && mode === 'mismatch' && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '60ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.08] p-5 mb-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-amber-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-100">Wrong account</p>
                <p className="text-xs text-amber-200/80 mt-1 leading-relaxed">
                  Sign out, then sign in with <span className="font-mono">{meta?.partnerEmail}</span> to redeem this code.
                </p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              localStorage.removeItem(AUTH_TOKEN_KEY)
              localStorage.removeItem(AGENT_DATA_KEY)
              window.location.href = `/login?redirect=${encodeURIComponent(`/redeem?code=${code}`)}`
            }}
            className="w-full h-11 bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            Sign out and continue
          </Button>
        </div>
      )}

      {/* Partner code but user isn't logged in — bounce them through login
          with a redirect back here so the partner flow can complete. */}
      {state === 'valid' && mode === 'partner-login' && meta && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/20 text-violet-200 text-xs font-bold uppercase tracking-wider mb-5">
            <Building2 className="h-3.5 w-3.5" />
            Agency partner · {tierLabel} · {meta.durationDays} days
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 mb-5">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45 mb-2">Invite reference</p>
            <p className="text-lg font-semibold text-white">{meta.label}</p>
            <p className="text-xs text-white/55 mt-1">Sign in as <span className="font-mono">{meta.partnerEmail}</span> and we'll drop you straight into setting up the new firm.</p>
          </div>

          <Button
            onClick={() => {
              window.location.href = `/login?redirect=${encodeURIComponent(`/redeem?code=${code}`)}`
            }}
            className="w-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 h-11 group"
          >
            Sign in to redeem
            <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
          </Button>
          <p className="mt-3 text-[11px] text-center text-white/40">
            Don't have an account on this email? Ask whoever sent you the link to issue a regular access code instead.
          </p>
        </div>
      )}

      {/* Default — not logged in (or non-partner code), use existing signup flow */}
      {state === 'valid' && mode === 'signup' && meta && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-400/20 text-emerald-300 text-xs font-bold uppercase tracking-wider mb-5">
            <Sparkles className="h-3.5 w-3.5" />
            Demo · {meta.durationDays} days · no card required
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-5 mb-5">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45 mb-2">Invited</p>
            <p className="text-lg font-semibold text-white">{meta.label}</p>
            <p className="text-xs text-white/55 mt-1">{tierLabel}-tier demo · expires in {meta.durationDays} days</p>
          </div>

          <ul className="space-y-3 mb-7">
            {[
              `Full ${tierLabel}-tier access`,
              'AI receptionist + outbound agent + dashboard',
              'Connect a real law firm website to test',
              'No charge, no auto-renew — convert any time',
            ].map((line, idx) => (
              <li
                key={line}
                className="flex items-start gap-3 text-sm text-white/85 opacity-0 animate-fade-in-up"
                style={{
                  animationDelay: `${160 + idx * 50}ms`,
                  animationFillMode: 'forwards',
                  animationTimingFunction: EASE_OUT_STRONG,
                }}
              >
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={2} />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={handleContinueSignup}
            className="w-full bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white hover:opacity-90 h-11 group"
          >
            Start your demo
            <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
          </Button>
          <p className="mt-3 text-[11px] text-center text-white/40">
            By continuing you agree to our Terms and Privacy Policy.
          </p>
        </div>
      )}

      {state === 'invalid' && (
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '60ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/[0.08] p-5 mb-5">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-full bg-rose-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-rose-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-rose-100">Code couldn't be redeemed</p>
                <p className="text-xs text-rose-200/80 mt-1 leading-relaxed">{error}</p>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="w-full h-11 bg-white/5 border-white/10 text-white hover:bg-white/10"
          >
            Go to home
          </Button>
        </div>
      )}
    </AuthShell>
  )
}

export { ACCESS_CODE_KEY, ACCESS_CODE_META_KEY }
